// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';


import { Moves, Hangtags } from '@gql/base-types';
import { isMoveLite } from '@utils/tiers';

import { useCustomerConfig, useRooftop } from '@store';
import { checkIfMoveCanBeAssignedOrUnassigned, findOffersInScenarios, formatMoveId } from '@services/moveService';
import { useFeaturePermission } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';

import { css } from '@emotion/css';
import { Avatar, Button, Chip, Grid, Paper, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { BiSolidCheckCircle, BiSolidLogInCircle, BiSolidUserCheck, BiSolidUserX } from 'react-icons/bi';

import { isOfferAccepted } from '@hopdrive/feature-driver-assignment';
import { useTools } from '@hooks/useTools';

import InfoItem from '@components/InfoItem';
import MoveStatusTracker from '@components/MoveStatusTracker';
import MoveTileHangtag from '@components/MoveTileHangtag';
import MoveTypeBadge from '@components/Badges/MoveTypeBadge';
import MoveChatBadge from '@features/moves/components/MoveChatBadge';
import Spacer from '@components/Utils/Spacer';
import { movePath } from '@features/products/productRoutes';

// TYPES ---------------------------------------------------------------------- //

interface DashboardMoveTileProps {
  move?: Moves;
  isAssigning?: boolean;
  handleAssignOrUnassignModalOpen?: (input: any) => void;
}

// COMPONENT ------------------------------------------------------------------ //

const DashboardMoveTile = ({ move, isAssigning, handleAssignOrUnassignModalOpen }: DashboardMoveTileProps) => {
  const theme = useTheme();
  const cls = useStyles(theme, { isConcierge: move?.consumer_type === 'customer' || move?.consumer_type === 'loaner' });
  const clsx = useSxStyles(theme);

  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();
  const hasDispatchingFeature = useFeaturePermission(FeatureName.DISPATCHING, rooftop?.id);
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const moveId: number | undefined = move?.id ?? undefined;
  const offers = findOffersInScenarios(move?.scenariotomoves || []);
  // The verdict offer: accepted-first, same rule as embed's tile and resolveDriverAssignment —
  // in a broadcast the newest offer can still be pending while an older one was taken.
  const verdictOffer = offers?.find(isOfferAccepted) ?? offers?.[0];

  const { getFormattedVehicleFromMove, getCancelHeaderFromMove, getCancelReasonFromMove, getCleansedPhoneNumber } =
    useTools();

  const assignOrUnassignDriver = () => {
    if (handleAssignOrUnassignModalOpen) handleAssignOrUnassignModalOpen({ move, offers });
  };

  return (
    <Paper variant='custom' sx={clsx.containerMain}>
      <Link
        data-testid="move-tile"
        to={movePath(moveId)}
        className={
          move?.consumer_type === 'customer' || move?.consumer_type === 'loaner' ? cls.consumerMove : cls.standardMove
        }
      >
        <Grid container spacing={1} alignItems='flex-start' wrap='nowrap' sx={clsx.top}>
          <Grid>
            {move?.consumer_type === 'customer' || move?.consumer_type === 'loaner' ? (
              <Tooltip title='Consumer Name & Phone' placement='top'>
                <div className={cls.consumer}>
                  <Typography sx={clsx.consumerNameText}>{move?.consumer_name}</Typography>
                  <Typography sx={clsx.consumerPhoneText}>{getCleansedPhoneNumber(move?.consumer_phone)}</Typography>
                </div>
              </Tooltip>
            ) : null}

            <Tooltip title='Vehicle Info' placement='top'>
              <Typography sx={clsx.vehicleText}>{getFormattedVehicleFromMove(move)}</Typography>
            </Tooltip>

            <Tooltip title='Lane Info' placement='top'>
              <Typography sx={clsx.laneText}>{move?.lane?.description}</Typography>
            </Tooltip>
          </Grid>

          <Grid size='grow' />

          {isMoveLite(move) && move?.lane?.duration_sec ? (
            <Grid>
              <Tooltip title='Move Duration' placement='top'>
                <Chip
                  label={`${Math.round(dayjs.duration(move?.lane?.duration_sec ?? 0, 'seconds').asMinutes())} min`}
                  variant='outlined'
                />
              </Tooltip>
            </Grid>
          ) : null}

          <Grid>
            <Tooltip title='Move Type' placement='top'>
              <MoveTypeBadge move={move} />
            </Tooltip>
          </Grid>

          {/* SMS Chat Badge - a per-order self-managed affordance */}
          {move?.consumer_pickup && isMoveLite(move) ? (
            <Grid sx={{ position: 'relative', zIndex: 2 }}>
              <MoveChatBadge moveId={move?.id} consumerPickup={move?.consumer_pickup} variant='icon' size='small' />
            </Grid>
          ) : null}
        </Grid>

        <Spacer size={1} />

        <MoveStatusTracker move={move} size='sm' />

        <Spacer size={1} />

        <Grid container spacing={2} alignItems='flex-start' wrap='nowrap' sx={clsx.bottom}>
          <Grid size='grow'>
            <InfoItem
              size={`sm`}
              keyText={`HopDrive ID`}
              valueText={formatMoveId(move?.id ?? 0, { isLite: isMoveLite(move) })}
            />

            <InfoItem size={`sm`} keyText={refLabel} valueText={move?.reference_num || `N/A`} />

            <InfoItem size={`sm`} keyText={`VIN`} valueText={move?.vehicle_vin || `N/A`} />

            <InfoItem size={`sm`} keyText={`Stock`} valueText={move?.vehicle_stock || `N/A`} />
          </Grid>

          <Grid size='grow'>
            <InfoItem
              size={`sm`}
              keyText={
                move?.consumer_type === `customer` && move?.consumer_at_pickup === 1 ? `Appointment` : `Ready By`
              }
              valueText={dayjs(move?.ready_by).format('M/D h:mm A z')}
            />

            <InfoItem
              size={`sm`}
              keyText={`Created By`}
              valueText={
                move?.createdBy
                  ? move?.createdBy
                  : move?.eventlogs && move?.eventlogs.length > 0
                    ? move?.eventlogs[0].user
                    : 'Unknown'
              }
            />

            <InfoItem size={`sm`} keyText={`Workflow`} valueText={move?.workflowset?.name || 'Unknown'} />
          </Grid>
        </Grid>

        {!move?.cancel_status && move?.status === 'delivery successful' ? <div className={cls.completed} /> : null}

        {move?.cancel_status ? (
          <div className={cls.canceled}>
            <div className={cls.canceledTitle}>
              <Typography sx={clsx.cancelText}>{getCancelHeaderFromMove(move)}</Typography>
            </div>

            <div className={cls.canceledSubtitle}>
              <Typography sx={clsx.cancelReasonText}>{getCancelReasonFromMove(move)}</Typography>
            </div>
          </div>
        ) : null}

        {move?.hangtags && move?.hangtags.length && move?.hangtags[0].type === `yard` ? (
          <MoveTileHangtag hangtag={move?.hangtags[0] as Hangtags} />
        ) : null}
      </Link>

      {isMoveLite(move) ? (
        <div className={cls.containerActions}>
          {isAssigning ? (
            <Typography sx={clsx.containerActionsTitle}>WORKING ON DRIVER ASSIGNMENT...</Typography>
          ) : move?.driver_id || offers?.length ? (
            <Tooltip
              placement='top'
              title={
                isOfferAccepted(verdictOffer)
                  ? `Accepted by Driver`
                  : `Offered to Driver`
              }
            >
              <div className={cls.containerActionsDriver}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: theme?.palette?.grey?.[300],
                  }}
                  src={move?.driver?.user?.avatar_url || verdictOffer?.driver?.user?.avatar_url || ''}
                  alt={move?.driver?.user?.display_name || verdictOffer?.driver?.user?.display_name || 'Unknown'}
                />

                <Typography sx={clsx.containerActionsDriverTitle}>
                  {move?.driver?.user?.display_name || verdictOffer?.driver?.user?.display_name || 'Unknown'}
                </Typography>

                {isOfferAccepted(verdictOffer) ? (
                  <BiSolidCheckCircle color={theme?.palette?.success?.main} size={20} />
                ) : (
                  <BiSolidLogInCircle color={theme?.palette?.info?.main} size={20} />
                )}

                <div className={cls.containerActionsDriverShadow} />
              </div>
            </Tooltip>
          ) : (
            <Typography sx={clsx.containerActionsTitle}>NOT ASSIGNED TO DRIVER</Typography>
          )}

          <div className={cls.containerActionsFlex} />

          <Tooltip placement='top' title={!hasDispatchingFeature ? 'You do not have permission to assign drivers' : 'Assign or unassign a driver to complete this move'}>
            <span>
              <Button
                loading={isAssigning}
                disabled={!hasDispatchingFeature || !checkIfMoveCanBeAssignedOrUnassigned(move) || isAssigning}
                startIcon={move?.driver_id || offers?.length ? <BiSolidUserX /> : <BiSolidUserCheck />}
                size='small'
                variant='contained'
                color={move?.driver_id || offers?.length ? `error` : move?.consumer_type ? `secondary` : `primary`}
                onClick={() => assignOrUnassignDriver()}
              >
                {move?.driver_id || offers?.length ? `Unassign Driver` : `Assign Driver`}
              </Button>
            </span>
          </Tooltip>
        </div>
      ) : null}
    </Paper>
  );
};

// STYLES --------------------------------------------------------------------- //

const useStyles = (theme?: Theme, options?: { isConcierge?: boolean }) => ({
  container: css`
    position: relative;
  `,

  standardMove: css`
    display: block;
    position: relative;
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
    user-select: none;
    transition: background-color 0.15s;
    &:hover {
      outline: 2px solid ${theme?.palette?.primary?.main};
      outline-offset: -2px;
      background-color: ${`${theme?.palette?.primary?.main}24`};
    }
  `,
  consumerMove: css`
    display: block;
    position: relative;
    overflow: hidden;
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
    user-select: none;
    transition: background-color 0.15s;
    &:hover {
      outline: 2px solid ${theme?.palette?.secondary?.main};
      outline-offset: -2px;
      background-color: ${`${theme?.palette?.secondary?.main}24`};
    }
  `,

  consumer: css`
    margin-bottom: 2px;
  `,

  completed: css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 8px;
    background-color: ${theme?.palette?.background?.paper};
    opacity: 0.5;
    pointer-events: none;
  `,

  canceled: css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 8px;
    background-color: ${theme?.palette?.utility?.darker}b4;
    ${theme?.breakpoints?.down('md')} {
      background-color: ${theme?.palette?.utility?.darker}c8;
    }
  `,
  canceledTitle: css`
    position: absolute;
    top: ${options?.isConcierge ? '13.75%' : '9%'};
    left: 0;
    right: 0;
  `,
  canceledSubtitle: css`
    position: absolute;
    top: ${options?.isConcierge ? '42.25%' : '34.5%'};
    left: 0;
    right: 0;
  `,

  containerActions: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px;
    border-top: 1px solid ${theme?.palette?.divider};
    border-radius: 0 0 10px 10px;
  `,
  containerActionsDriver: css`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: ${theme?.palette?.grey?.[50]};
    padding: 0 8px 0 0;
    border-radius: 40px;
    cursor: pointer;
  `,
  containerActionsDriverShadow: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    box-shadow: 0px 0px 6px 2px #00000030 inset;
  `,
  containerActionsFlex: css`
    flex: 1;
  `,
});

const useSxStyles = (theme?: Theme) => ({
  containerMain: {
    position: 'relative',
  },

  top: {
    minWidth: '100%',
    marginBottom: theme?.spacing(0.5),
  },
  bottom: {
    minWidth: '100%',
    marginTop: theme?.spacing(0.5),
  },

  consumerNameText: {
    display: 'inline-block',
    marginRight: '4px',
    lineHeight: 1,
    fontSize: '16px',
    fontWeight: 700,
    color: theme?.palette?.secondary?.main,
  },
  consumerPhoneText: {
    display: 'inline-block',
    lineHeight: 1,
    fontSize: '16px',
    fontWeight: 500,
    color: theme?.palette?.secondary?.main,
  },

  vehicleText: {
    marginBottom: '2px',
    lineHeight: 1,
    fontSize: '16px',
    fontWeight: 700,
  },

  laneText: {
    lineHeight: 1,
    fontSize: '12px',
  },

  cancelText: {
    lineHeight: 1,
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 900,
    color: theme?.palette?.text?.contrastPrimary,
  },
  cancelReasonText: {
    lineHeight: 1,
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: 500,
    color: theme?.palette?.text?.contrastPrimary,
  },

  containerActionsDriverTitle: {
    fontSize: '14px',
    fontWeight: 600,
  },
  containerActionsTitle: {
    marginLeft: '4px',
    fontSize: '14px',
    fontWeight: 600,
    color: theme?.palette?.text?.disabled,
  },
});

// EXPORT -------------------------------------------------------------------- //

// Memoized: the dashboard re-renders many times during load (query status flips, stream
// broadcasts, context churn), and each pass re-rendered every tile — ~100 heavy MUI trees
// per pass, which under continuous move activity merged into multi-second synchronous
// tasks. Tile props are stable across those passes (`move` identity is preserved by
// DashboardContent's equality guards; the modal opener is wrapped in useCallback), so
// memo lets unchanged tiles skip entirely.
export default React.memo(DashboardMoveTile);
